import axios from 'axios';
import { apiClient } from './client';
import type { FeedbackMediaSlot, FeedbackType } from '../features/feedback/feedbackForm';

export const FEEDBACK_SOURCE_LABEL = 'ABPL UPC Management' as const;

export interface FeedbackLocationContext {
  product_area: string;
  route_path: string;
  page_title?: string;
  client_timestamp: string;
}

export interface FeedbackMediaPayload {
  slot: FeedbackMediaSlot;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  content_base64: string;
  duration_seconds?: number;
}

export interface FeedbackSubmissionPayload {
  submission_id: string;
  feedback_type: FeedbackType;
  written_feedback: string;
  source_label: typeof FEEDBACK_SOURCE_LABEL;
  location_context: FeedbackLocationContext;
  media?: FeedbackMediaPayload[];
}

export interface FeedbackSubmissionAcceptedResponse {
  status: 'accepted';
  destination_space: 'm360 abpl/upc / ABPL UPC User Feedback';
  journey_item_id: string;
}

interface FeedbackSubmissionFailureResponse {
  message?: string;
  details?: {
    status?: 'failed';
    retryable?: boolean;
    submission_id?: string;
    submitted_feedback?: {
      written_feedback?: string;
    };
  };
}

export class FeedbackSubmissionError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
    readonly submissionId?: string
  ) {
    super(message);
    this.name = 'FeedbackSubmissionError';
  }
}

export function createFeedbackSubmissionId() {
  const randomId =
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

  return `abpl-upc-feedback:${randomId}`;
}

export async function submitFeedback(
  payload: FeedbackSubmissionPayload
): Promise<FeedbackSubmissionAcceptedResponse> {
  try {
    const response = await apiClient.post<FeedbackSubmissionAcceptedResponse>(
      '/products/feedback',
      payload
    );

    if (response.data.status !== 'accepted') {
      throw new FeedbackSubmissionError('Journey did not accept the feedback submission.', true);
    }

    return response.data;
  } catch (error) {
    if (error instanceof FeedbackSubmissionError) {
      throw error;
    }

    if (axios.isAxiosError<FeedbackSubmissionFailureResponse>(error)) {
      const data = error.response?.data;
      throw new FeedbackSubmissionError(
        data?.message || 'Feedback submission failed. Please review and try again.',
        data?.details?.retryable ?? true,
        data?.details?.submission_id
      );
    }

    throw new FeedbackSubmissionError(
      'Feedback submission failed. Please check your connection and try again.',
      true
    );
  }
}

export function getFeedbackLocationContext(now: Date = new Date()): FeedbackLocationContext {
  return {
    product_area: 'UPC Management',
    route_path: window.location.pathname,
    page_title: document.title || undefined,
    client_timestamp: now.toISOString(),
  };
}

export async function toFeedbackMediaPayload({
  slot,
  file,
  durationSeconds,
}: {
  slot: FeedbackMediaSlot;
  file: File;
  durationSeconds?: number;
}): Promise<FeedbackMediaPayload> {
  return {
    slot,
    file_name: file.name,
    mime_type: file.type,
    size_bytes: file.size,
    content_base64: await readFileAsBase64(file),
    duration_seconds: durationSeconds,
  };
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Unable to read attached media.'));
        return;
      }

      resolve(reader.result.replace(/^data:[^;]+;base64,/, ''));
    };
    reader.onerror = () => {
      reject(new Error('Unable to read attached media.'));
    };
    reader.readAsDataURL(file);
  });
}
